import type { Esteira } from '../../interfaces/Esteira';

/**
 * As fases do Telão — cada uma agrupa as esteiras de um mesmo momento do fluxo da oficina.
 *
 * at-130 (23/10/2026): o agrupamento foi proposto na Fase 1 e aceito pelo Victor ao escolher o
 * protótipo. Não é medição: é leitura do fluxo. O mapa é por CHAVE, nunca por nome nem por id
 * (o nome o ADMIN edita; o id muda entre ambientes).
 *
 * Esteira ativa que não estiver aqui cai em "Outras esteiras", que só aparece quando tem veículo
 * — é o que impede uma esteira nova, criada em Fluxos de Esteiras, de sumir do telão.
 * `ENTREGA` nunca entra: o veículo entregue não está mais na oficina (o kanban faz igual).
 */
export interface FaseDoTelao {
  id: string;
  nome: string;
  chaves: string[];
}

export const FASES_DO_TELAO: FaseDoTelao[] = [
  { id: 'ENTRADA', nome: 'Entrada e regulação', chaves: ['AGUARDANDO', 'ORCAMENTO', 'SINDICANCIA', 'INDENIZACAO_INTEGRAL', 'NEGATIVA'] },
  { id: 'CARROCERIA', nome: 'Desmontagem e carroceria', chaves: ['DESMONTAGEM', 'FUNILARIA', 'PREPARACAO_PINTURA'] },
  { id: 'PINTURA', nome: 'Pintura e acabamento', chaves: ['PINTURA', 'POLIMENTO', 'HIGIENIZACAO'] },
  { id: 'MECANICA', nome: 'Mecânica e montagem', chaves: ['MECANICA', 'ELETRICA', 'MONTAGEM', 'CORRECAO', 'SERVICO_EXTERNO'] },
  { id: 'FINAL', nome: 'Finalização e entrega', chaves: ['TESTE_FINAL', 'CHECKLIST', 'AGUARDANDO_PROXIMA_ETAPA', 'AGUARDANDO_ENTREGA'] },
];

export const FASE_DE_SOBRA: FaseDoTelao = { id: 'OUTRAS', nome: 'Outras esteiras', chaves: [] };

export const ESTEIRAS_FORA_DO_TELAO = ['ENTREGA'];

export interface EsteiraNaFase<C> {
  esteira: Esteira;
  carros: C[];
}

export interface FaseMontada<C> {
  fase: FaseDoTelao;
  esteiras: EsteiraNaFase<C>[];
  total: number;
}

/**
 * Distribui as esteiras ativas e os veículos pelas fases.
 *
 * - Dentro da fase, a ordem é a do cadastro (`esteira.ordem`), não a do mapa: é a que o ADMIN
 *   escolheu em Fluxos de Esteiras.
 * - Esteira sem veículo continua na fase (mantém a posição fixa na TV).
 * - A fase de sobra só existe quando alguma esteira dela tem veículo.
 */
export function agruparEmFases<C extends { esteiraId?: string | null }>(
  esteirasAtivas: Esteira[],
  carros: C[],
): FaseMontada<C>[] {
  const porEsteira = new Map<string, C[]>();
  carros.forEach((c) => {
    if (!c.esteiraId) return;
    const lista = porEsteira.get(c.esteiraId) ?? [];
    lista.push(c);
    porEsteira.set(c.esteiraId, lista);
  });

  const visiveis = esteirasAtivas
    .filter((e) => !ESTEIRAS_FORA_DO_TELAO.includes(e.chave))
    .slice()
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

  const chavesMapeadas = new Set(FASES_DO_TELAO.flatMap((f) => f.chaves));
  const montar = (fase: FaseDoTelao, lista: Esteira[]): FaseMontada<C> => {
    const esteiras = lista.map((esteira) => ({ esteira, carros: porEsteira.get(esteira.id) ?? [] }));
    return { fase, esteiras, total: esteiras.reduce((n, e) => n + e.carros.length, 0) };
  };

  const fases = FASES_DO_TELAO.map((fase) =>
    montar(fase, visiveis.filter((e) => fase.chaves.includes(e.chave))),
  );
  const sobra = montar(FASE_DE_SOBRA, visiveis.filter((e) => !chavesMapeadas.has(e.chave)));
  return sobra.total > 0
    ? [...fases, { ...sobra, esteiras: sobra.esteiras.filter((e) => e.carros.length > 0) }]
    : fases;
}
